import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Youtube, Instagram, ArrowRight, ExternalLink } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cepres-edu/artifacts/tldo3ilo_ciu-logo.png';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    about: [
      { name: 'Our Story', path: '/about' },
      { name: 'Why Choose CIU', path: '/why-ciu' },
      { name: 'Accreditation', path: '/accreditation' },
      { name: 'Research', path: '/research' },
    ],
    programs: [
      { name: 'Degree Programs', path: '/programs' },
      { name: 'Vocational Programs', path: '/vocational' },
      { name: 'Campus Services', path: '/services' },
      { name: 'Student Stories', path: '/stories' },
    ],
    admissions: [
      { name: 'How to Apply', path: '/admissions' },
      { name: 'Requirements', path: '/admissions#requirements' },
      { name: 'News & Blog', path: '/blog' },
      { name: 'Contact Us', path: '/contact' },
    ],
  };

  return (
    <footer data-testid="footer" className="bg-[#0D4A2E] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="CEPRES International University" 
                className="h-16 w-auto object-contain bg-white rounded-lg p-2"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-sm">
              Stimulating Public Health Quality through Research, Training, and Service Improvement since 2014.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Facebook, label: 'Facebook' },
                { icon: Twitter, label: 'Twitter' },
                { icon: Linkedin, label: 'LinkedIn' },
                { icon: Youtube, label: 'YouTube' },
                { icon: Instagram, label: 'Instagram' },
              ].map(({ icon: Icon, label }) => (
                <a 
                  key={label}
                  href="#" 
                  data-testid={`social-${label.toLowerCase()}`}
                  aria-label={label}
                  className="w-10 h-10 flex items-center justify-center border border-white/20 text-white/60 hover:bg-[#C5A028] hover:border-[#C5A028] hover:text-[#1A1A1A] transition-all duration-300"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* About Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-[#C5A028]">
              About CIU
            </h3>
            <ul className="space-y-3">
              {footerLinks.about.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-[#C5A028] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-[#C5A028]">
              Programs
            </h3>
            <ul className="space-y-3">
              {footerLinks.programs.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-[#C5A028] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Admissions Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-6 text-[#C5A028]">
              Admissions
            </h3>
            <ul className="space-y-3">
              {footerLinks.admissions.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-[#C5A028] transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://cnte.ciu.edu.lr"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-cnte-link"
                  className="inline-flex items-center gap-1.5 text-white/60 hover:text-[#C5A028] transition-colors duration-200 text-sm"
                >
                  Student Portal (CNTE)
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Info Row */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#C5A028]" />
              <span className="text-white/60 text-sm">Gbarnga City, Bong County, Liberia</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#C5A028]" />
              <a href="tel:+231886608842" data-testid="footer-phone" className="text-white/60 text-sm hover:text-[#C5A028] transition-colors">+231 886 608 842</a>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#C5A028]" />
              <span className="text-white/60 text-sm">info@cepres.edu.lr</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {currentYear} CEPRES International University. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-white/40 hover:text-[#C5A028] transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-white/40 hover:text-[#C5A028] transition-colors duration-200">
                Terms of Service
              </a>
              <a href="#" className="text-white/40 hover:text-[#C5A028] transition-colors duration-200">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
